/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import {
  Row,
  Col,
  Space,
  Descriptions,
  Table,
  Collapse,
  Tabs,
  Button,
  Typography,
  message,
  Flex,
} from "antd";

import {
  ArrowLeftOutlined,
  CopyOutlined,
  EditOutlined,
} from "@ant-design/icons";

import MasterProcessService from "../../../service/ERP/MasterProcess.service.js";

import dayjs from "dayjs";
import useGoBack from "../../../hooks/useGoBack";
import { percentColumn, ratioInKg, calcRawWeight } from "./model";
import ActionGroup, { PrintButton, ACTION_BTN as AG_BTN } from "../../../components/action-group/ActionGroup";

const masterProcessService = MasterProcessService();
const gotoFrom = "/ERP/master-process";

const { Title } = Typography;

const groupDetailByStep = (detail = [], remarkSteps = []) => {
  const remarkMap = new Map();
  (remarkSteps || []).forEach((r) =>
    remarkMap.set(Number(r?.stepno || 0), r?.remark || ""),
  );

  const grouped = new Map();
  (detail || []).forEach((item) => {
    const stepNo = Number(item?.stepno || 1) || 1;
    if (!grouped.has(stepNo)) grouped.set(stepNo, []);
    grouped.get(stepNo).push(item);
  });

  return Array.from(grouped.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([stepNo, items]) => ({
      stepNo,
      remark: remarkMap.get(stepNo) || "",
      materials: items
        .slice()
        .sort((a, b) => Number(a?.mpno || 0) - Number(b?.mpno || 0))
        .map((item, idx) => ({ ...item, seq: idx + 1 })),
    }));
};

const buildMaterialViewColumns = (totalRatio) => [
  { title: "No.", dataIndex: "seq", key: "seq", align: "center", width: 60 },
  { title: "รหัสวัตถุดิบ", dataIndex: "stcode", key: "stcode", width: 150 },
  { title: "ชื่อวัตถุดิบ", dataIndex: "stname", key: "stname" },
  {
    title: "น้ำหนักเข้าสูตร",
    dataIndex: "ratio",
    key: "ratio",
    align: "center",
    width: 130,
  },
  percentColumn(totalRatio),
  {
    title: "Multiply",
    dataIndex: "multiply",
    key: "multiply",
    align: "center",
    width: 110,
  },
  {
    title: "Yield (%)",
    dataIndex: "yield",
    key: "yield",
    align: "center",
    width: 110,
  },
  {
    title: "น้ำหนักวัตถุดิบ",
    key: "qty",
    align: "center",
    width: 150,
    render: (_, r) => calcRawWeight(r),
  },
  { title: "ขั้นตอน", dataIndex: "method", key: "method", width: 200 },
  { title: "ชนิดวัตถุดิบ", dataIndex: "sttype", key: "sttype", width: 130 },
  { title: "หน่วย", dataIndex: "unit", key: "unit", width: 90 },
];

const parameterViewColumns = [
  {
    title: "ลำดับ",
    dataIndex: "para_no",
    key: "para_no",
    align: "center",
    width: 90,
  },
  { title: "การตรวจสอบ", dataIndex: "inspection", key: "inspection" },
  { title: "ค่า spec", dataIndex: "spec", key: "spec", width: 180 },
  { title: "target", dataIndex: "target", key: "target", width: 180 },
];

const mngConfig = {
  title: "",
  textOk: null,
  textCancel: null,
  action: "view",
  fg_code: null,
  mp_id: null,
};

export default function MasterProcessView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = location.state || { config: null };

  const [loading, setLoading] = useState(false);
  const [versions, setVersions] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [header, setHeader] = useState({});
  const [steps, setSteps] = useState([]);
  const [parameters, setParameters] = useState([]);

  const tabItems = useMemo(
    () =>
      versions.map((v, idx) => ({
        key: String(v.mp_id),
        label: `เวอร์ชั่น ${v.version}`,
      })),
    [versions],
  );

  /* กลับไปหน้าที่กดรหัสเอกสารมา ถ้าไม่มีประวัติค่อยกลับหน้ารายการ */
  const handleClose = useGoBack(gotoFrom);

  const loadVersion = async (fgCode, mpId = null) => {
    setLoading(true);
    try {
      const res = await masterProcessService.get(fgCode, mpId);
      const {
        data: {
          header: h,
          detail,
          remark_step,
          parameters: params,
          versions: versionList,
        },
      } = res.data;

      setVersions(versionList || []);
      setHeader(h || {});
      setSteps(groupDetailByStep(detail, remark_step));
      setParameters(params || []);
      setActiveKey(h?.mp_id != null ? String(h.mp_id) : null);
    } catch (err) {
      console.warn(err);
      message.error("Error getting master process information.");
    } finally {
      setLoading(false);
    }
  };

  const onChangeTab = (key) => {
    loadVersion(config?.fg_code, key);
  };

  useEffect(() => {
    if (!config?.fg_code) {
      handleClose();
      return;
    }
    loadVersion(config.fg_code);

    return () => {};
  }, []);

  const handleEditMasterProcess = () => {
    navigate("/ERP/master-process/manage/edit", {
      state: {
        config: {
          ...mngConfig,
          title: "แก้ไข Master Process",
          action: "edit",
          fg_code: header?.fg_code,
          mp_id: header?.mp_id,
        },
      },
    });
  };

  const handleNewVersionMasterProcess = () => {
    navigate("/ERP/master-process/manage/edit", {
      state: {
        config: {
          ...mngConfig,
          title: "สร้างเวอร์ชั่นใหม่ Master Process",
          action: "copy",
          fg_code: header?.fg_code,
          mp_id: header?.mp_id,
        },
      },
    });
  };

  const masterItems = [
    { label: "รหัสสินค้า", children: header?.fg_code },
    {
      label: "ชื่อสินค้า",
      children: header?.fg_name,
      span: { xs: 1, sm: 2, md: 2, lg: 2, xl: 2, xxl: 2 },
    },
    {
      label: "เวอร์ชั่น",
      children: header?.version ? `v${header.version}` : "-",
    },
    {
      label: "อัพเดทล่าสุด",
      children: header?.updated_date
        ? dayjs(header.updated_date).format("DD/MM/YYYY HH:mm")
        : "-",
    },
    { label: "อัพเดทโดย", children: header?.updated_by || "-" },
    {
      label: "Remark",
      children: header?.remark || "-",
      span: { xs: 1, sm: 2, md: 3, lg: 3, xl: 3, xxl: 3 },
    },
  ];

  const stepItems = [
    {
      key: "1",
      label: "ขั้นตอนการทำงานผลิต",
      children: (
        <Space direction="vertical" size="middle" style={{ display: "flex" }}>
          {steps.map((step) => (
            <div key={step.stepNo}>
              <Title level={5} className="m-0 !text-zinc-800" style={{ marginBottom: 8 }}>
                Step {step.stepNo}
              </Title>
              <Table
                style={{ backgroundColor: "#fafafa" }}
                bordered
                dataSource={step.materials}
                columns={buildMaterialViewColumns(
                  steps.reduce((sum, st) => sum + (st.materials || []).reduce((a, m) => a + ratioInKg(m), 0), 0),
                )}
                pagination={false}
                rowKey={(r) => r.id || r.seq}
                scroll={{ x: "max-content" }}
                size="small"
              />
              {step.remark && (
                <div style={{ marginTop: 8, color: "rgba(0,0,0,0.65)" }}>
                  <b>การทำงาน:</b> {step.remark}
                </div>
              )}
            </div>
          ))}
        </Space>
      ),
      disabled: true,
      showArrow: false,
    },
  ];

  const parametersQC = (parameters || []).filter(
    (p) => (p?.para_type || "QC") !== "PROD",
  );
  const parametersProd = (parameters || []).filter(
    (p) => p?.para_type === "PROD",
  );

  const parameterItems = [
    {
      key: "1",
      label: "Parameter QC",
      children: (
        <Table
          style={{ backgroundColor: "#fafafa" }}
          bordered
          dataSource={parametersQC}
          columns={parameterViewColumns}
          pagination={false}
          rowKey={(r) => r.para_no}
          scroll={{ x: "max-content" }}
          size="small"
        />
      ),
      disabled: true,
      showArrow: false,
    },
  ];

  const parameterProdItems = [
    {
      key: "1",
      label: "Parameter ผลิต",
      children: (
        <Table
          style={{ backgroundColor: "#fafafa" }}
          bordered
          dataSource={parametersProd}
          columns={parameterViewColumns}
          pagination={false}
          rowKey={(r) => r.para_no}
          scroll={{ x: "max-content" }}
          size="small"
        />
      ),
      disabled: true,
      showArrow: false,
    },
  ];

  const ButtonActionLeft = (
    <Space
      gap="small"
      align="center"
      style={{ display: "flex", justifyContent: "start" }}
    >
      <Button
        style={{ width: 120 }}
        icon={<ArrowLeftOutlined />}
        onClick={handleClose}
      >
        Back
      </Button>
    </Space>
  );

  const hasVersion = versions.length > 0;

  const handlePrintMasterProcess = () => {
    if (!header?.fg_code) return;
    const mpPart = header?.mp_id != null ? `/${header.mp_id}` : "";
    window.open(`/mp-print/${encodeURIComponent(header.fg_code)}${mpPart}`, "_blank");
  };

  const ButtonActionRight = (
    <Space
      gap="small"
      align="center"
      className="flex justify-start lg:justify-end md:justify-end"
    >
      <Flex gap={6} wrap="wrap" align="center">
        <ActionGroup type="print">
          <PrintButton onClick={handlePrintMasterProcess} disabled={!header?.fg_code}>
            Print
          </PrintButton>
        </ActionGroup>
        <ActionGroup type="action">
          <Button
            type="primary"
            className="bn-center justify-center"
            style={AG_BTN}
            icon={<CopyOutlined />}
            onClick={handleNewVersionMasterProcess}
          >
            New Version
          </Button>
          {hasVersion && (
            <Button
              className="bn-center justify-center"
              style={{ ...AG_BTN, borderColor: "#52c41a", color: "#fff", background: "#52c41a" }}
              icon={<EditOutlined />}
              onClick={handleEditMasterProcess}
            >
              Edit Master Process
            </Button>
          )}
        </ActionGroup>
      </Flex>
    </Space>
  );

  return (
    <Space
      direction="vertical"
      size="middle"
      style={{
        display: "flex",
        position: "relative",
        paddingInline: "1.34rem",
      }}
      className="master-process-view"
    >
      <Row gutter={[{ xs: 32, sm: 32, md: 32, lg: 12, xl: 12 }, 8]} className="m-0">
        <Col xs={24} sm={24} md={12} lg={12} xl={12} className="px-0">
          {ButtonActionLeft}
        </Col>
        <Col xs={24} sm={24} md={12} lg={12} xl={12} className="px-0">
          {ButtonActionRight}
        </Col>
      </Row>

      <Tabs
        hideAdd
        onChange={onChangeTab}
        activeKey={activeKey}
        type="card"
        items={tabItems}
      />

      <Descriptions
        bordered
        column={{ xs: 1, sm: 2, md: 3, lg: 3, xl: 3, xxl: 3 }}
        items={masterItems}
      />

      <Collapse
        size="large"
        className="view-collapse-sp"
        collapsible="disabled"
        activeKey="1"
        bordered={false}
        style={{ backgroundColor: "transparent" }}
        items={stepItems}
      />

      <Collapse
        size="large"
        className="view-collapse-sp"
        collapsible="disabled"
        activeKey="1"
        bordered={false}
        style={{ backgroundColor: "transparent" }}
        items={parameterProdItems}
      />

      <Collapse
        size="large"
        className="view-collapse-sp"
        collapsible="disabled"
        activeKey="1"
        bordered={false}
        style={{ backgroundColor: "transparent" }}
        items={parameterItems}
      />

      <Row gutter={[{ xs: 32, sm: 32, md: 32, lg: 12, xl: 12 }, 8]} className="mx-0 mb-4">
        <Col xs={24} sm={24} md={12} lg={12} xl={12} className="px-0">
          {ButtonActionLeft}
        </Col>
      </Row>
    </Space>
  );
}
